// Copyright ©️ 2026 eVolve MEP, LLC

import type { AutodeskExternalId } from '@evolvemep/api-ts';

import type { APSViewerStore } from 'components/Autodesk/extensions/APSViewerStoreExtension/createAPSViewerStore';
import type { ForgeViewerSelection } from 'components/Autodesk/extensions/SelectionToolsExtension/types';
import { addViewerListener } from 'components/Autodesk/extensions/utils';
import { getBulkPropertiesBatched, getExternalIdMappingAsync } from 'components/Autodesk/extensions/viewerHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { THREE } from 'components/Autodesk/viewerThree';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';

const { Autodesk } = window;

/**
 * An extension that bridges Autodesk Viewer events into the `APSViewerStore`,
 * keeping viewer-wide React state (isolation, selection) automatically in sync
 * without requiring manual render calls or event bus dispatches from other extensions.
 *
 * One instance is loaded per viewer, each receiving its own store via options.
 * This makes multiple simultaneous viewers safe: each has isolated state.
 */
export class APSViewerStoreExtension extends Autodesk.Viewing.Extension {
  private store: APSViewerStore;
  private cleanUpFns: (() => void)[] = [];
  private selectionChangeSeq = 0;

  constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: { store: APSViewerStore }) {
    super(viewer, options);
    this.store = options.store;

    this.onIsolate = this.onIsolate.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
    this.onModelRootLoaded = this.onModelRootLoaded.bind(this);
    this.onModelUnloaded = this.onModelUnloaded.bind(this);
  }

  load() {
    this.store.registerCommands({
      isolate: (dbIds, shouldFitToView?: boolean) => {
        this.viewer.isolate(dbIds);
        if (shouldFitToView) {
          this.viewer.fitToView(dbIds);
        }
      },
      fitToView: (dbIds) => this.viewer.fitToView(dbIds),
      showAll: () => this.viewer.showAll(),
      setGhosting: (enabled) => this.viewer.setGhosting(enabled),
      clearSelection: () => this.viewer.clearSelection(),
      setThemingColor: (dbIds, color, recursive) => {
        const threeColor = new THREE.Color(String(color));
        const colorVector = new THREE.Vector4(threeColor.r, threeColor.g, threeColor.b, 1);
        dbIds.forEach((dbId) => {
          this.viewer.setThemingColor(dbId, colorVector, this.viewer.model, recursive);
        });
      },
      clearThemingColors: () => this.viewer.clearThemingColors(this.viewer.model),
      hide: (dbIds) => this.viewer.hide(dbIds),
      show: (dbIds) => this.viewer.show(dbIds),
      lockSelection: (dbIds, locked) => this.viewer.lockSelection(dbIds, locked),
      setActiveNavigationTool: (toolName) => this.viewer.setActiveNavigationTool(toolName),
      getBulkProperties: (dbIds, options) => getBulkPropertiesBatched(this.viewer.model, dbIds, options),
    });

    this.cleanUpFns.push(
      addViewerListener(this.viewer, Autodesk.Viewing.ISOLATE_EVENT, this.onIsolate),
      addViewerListener(this.viewer, Autodesk.Viewing.SELECTION_CHANGED_EVENT, this.onSelectionChanged),
      addViewerListener(this.viewer, Autodesk.Viewing.MODEL_UNLOADED_EVENT, this.onModelUnloaded),
    );

    if (isNotNil(this.viewer.model)) {
      void this.onModelRootLoaded();
    } else {
      this.cleanUpFns.push(
        addViewerListener(this.viewer, Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT, this.onModelRootLoaded),
      );
    }

    return true;
  }

  unload() {
    this.store.unregisterCommands();
    this.store.clear();
    this.cleanUpFns.forEach((fn) => fn());
    this.cleanUpFns = [];
    return true;
  }

  private onIsolate(event: { nodeIdArray: AutodeskDbId[] }) {
    this.store.setIsolatedDbIds(event.nodeIdArray);
  }

  private async onSelectionChanged() {
    // Use a sequence number to avoid race conditions.
    const seq = ++this.selectionChangeSeq;
    const selection = this.viewer.getSelection();

    // getBulkPropertiesAsync will return EVERYTHING if given an empty array. So return early in that case.
    if (hasLengthExactly(selection, 0)) {
      this.store.setSelection([]);
      return;
    }

    const props = await getBulkPropertiesBatched(this.viewer.model, selection, { propFilter: ['externalId'] });

    // If the sequence number has changed since this call was started, this call is stale. Bail out.
    if (seq !== this.selectionChangeSeq) return;

    const result: ForgeViewerSelection[] = props.map(({ dbId, externalId }) => ({
      dbId,
      externalId: externalId as AutodeskExternalId,
    }));

    this.store.setSelection(result);
  }

  private async onModelRootLoaded() {
    const externalIdLookup = await getExternalIdMappingAsync(this.viewer.model);
    this.store.setExternalIdLookup(externalIdLookup);
  }

  private onModelUnloaded() {
    this.store.clear();
  }
}
